import type { Aviso } from '../../api/operaciones';
import type { Gravedad, RolAviso, TipoAviso } from '../../api/ops/entregas';
import type { Rol } from '../../api/operaciones';
import { cuando } from '../../logica/fechasCortas';

/**
 * Reglas sin estilos de «Avisos» (5.1 «Tus avisos» y 5.2 «Quién recibe avisos»). Las decide la API; aquí solo se
 * reflejan para no ofrecer lo que la API va a rechazar (un crítico no se silencia, un tipo no se queda sin roles).
 */

export const PERMISOS_AVISOS = {
    recibir: 'entregas.avisos.recibir',
    configurar: 'entregas.avisos.configurar',
} as const;

/** Filtros de «Tus avisos» sin cifras (G121, G140): solo «Sin leer» lleva la suya, la de `getMyNoticeSummary`. */
export const FILTROS_AVISOS = ['Todos', 'Sin leer', 'Archivados', 'Resueltos'] as const;
export type FiltroAvisos = 0 | 1 | 2 | 3;

export interface ConsultaAvisos {
    state?: 'unread' | 'archived';
    severity?: Gravedad;
    status?: 'resolved';
}

/** La consulta de `listMyNotices` de cada filtro y gravedad («Ver los avisos»). */
export function consultaDeFiltro(filtro: FiltroAvisos, gravedad: Gravedad | null): ConsultaAvisos {
    const base: ConsultaAvisos = gravedad === null ? {} : { severity: gravedad };
    switch (filtro) {
        case 1:
            return { ...base, state: 'unread' };
        case 2:
            return { ...base, state: 'archived' };
        case 3:
            return { ...base, status: 'resolved' };
        default:
            return base;
    }
}

/**
 * Lo que se pinta de una página: «Resueltos» pide solo los resueltos (`status=resolved`, G394) y enseña solo los que no están abiertos; «Todos» y «Sin leer» no enseñan los archivados.
 */
export function avisosDelFiltro(filtro: FiltroAvisos, avisos: readonly Aviso[]): Aviso[] {
    switch (filtro) {
        case 2:
            return avisos.filter((a) => a.state === 'archived');
        case 3:
            return avisos.filter((a) => a.status !== 'open');
        default:
            return avisos.filter((a) => a.state !== 'archived');
    }
}

/** ¿Se puede silenciar? Los críticos nunca (409, pregunta 5) ni los que el tipo marca como no silenciables. */
export function sePuedeSilenciar(tipo: { severity: Gravedad; mutable: boolean }): boolean {
    return tipo.mutable && tipo.severity !== 'critical';
}

export interface GrupoDia {
    titulo: string;
    avisos: Aviso[];
}

/** Avisos por día: Hoy, Ayer y Antes, del más reciente al más antiguo (el orden de la API). */
export function porDia(avisos: readonly Aviso[], ahora = new Date()): GrupoDia[] {
    const grupos: GrupoDia[] = [];
    for (const a of avisos) {
        const dia = cuando(a.last_occurred_at ?? a.created_at, ahora);
        const titulo = dia === 'Hoy' || dia === 'Ayer' ? dia : 'Antes';
        const g = grupos.find((x) => x.titulo === titulo);
        if (g === undefined) {
            grupos.push({ titulo, avisos: [a] });
        } else {
            g.avisos.push(a);
        }
    }
    return grupos;
}

// ---- 5.2 Quién recibe avisos ----

export interface RolDeAvisos {
    ref: RolAviso;
    nombre: string;
    propio: boolean;
}

/**
 * Los roles de la izquierda: el propietario (que no está en `listRoles`) y los de `listRoles` con su referencia de
 * Entregas (`RoleRef`): el código en los predefinidos y el id en los propios (G37).
 */
export function rolesDeAvisos(roles: readonly Rol[]): RolDeAvisos[] {
    const lista: RolDeAvisos[] = roles.map((r) => ({ ref: r.kind === 'custom' ? r.id : r.code, nombre: r.name, propio: r.kind === 'custom' }));
    // El propietario va primero (G394).
    const propietario = lista.find((r) => r.ref === 'owner') ?? { ref: 'owner', nombre: 'Propietario', propio: false };
    return [propietario, ...lista.filter((r) => r.ref !== 'owner')];
}

/** «Siempre» con candado: un crítico llega siempre al propietario. */
export function bloqueadoPara(tipo: Pick<TipoAviso, 'severity'>, rol: RolAviso): boolean {
    return tipo.severity === 'critical' && rol === 'owner';
}

/** Cuántos tipos recibe un rol («Recibe 12 de 13»). */
export function cuantosRecibe(tipos: readonly TipoAviso[], rol: RolAviso): number {
    return tipos.filter((t) => t.roles.includes(rol) || bloqueadoPara(t, rol)).length;
}

export type CambioRoles = { roles: RolAviso[] } | { error: string };

/** Los roles de un tipo al encender o apagar uno: nunca sin nadie (al menos un rol) ni un crítico sin el propietario. */
export function rolesTrasCambio(tipo: Pick<TipoAviso, 'severity' | 'roles'>, rol: RolAviso, encender: boolean): CambioRoles {
    if (!encender && bloqueadoPara(tipo, rol)) {
        return { error: 'Los avisos críticos llegan siempre al propietario.' };
    }
    const roles = encender ? [...new Set([...tipo.roles, rol])] : tipo.roles.filter((r) => r !== rol);
    if (roles.length === 0) {
        return { error: 'Cada aviso tiene que llegar al menos a un rol.' };
    }
    return { roles };
}

/** Versiones leídas de cada tipo, para `updateRoleNoticeTypes` (`base_versions`, G300). */
export function versionesBase(tipos: readonly TipoAviso[]): Record<string, number> {
    return Object.fromEntries(tipos.map((t) => [t.notice_type, t.version]));
}

/** «Activar todos»: todos los tipos para ese rol. */
export function tiposDeActivarTodos(tipos: readonly TipoAviso[]): string[] {
    return tipos.map((t) => t.notice_type);
}

/** «Copiar de otro rol»: los tipos que recibe el rol de origen (más los críticos si el destino es el propietario). */
export function tiposCopiados(tipos: readonly TipoAviso[], origen: RolAviso, destino: RolAviso): string[] {
    return tipos.filter((t) => t.roles.includes(origen) || bloqueadoPara(t, destino)).map((t) => t.notice_type);
}

/** Tipos agrupados por módulo, en el orden en que llegan (G299: se agrupan por `module`). */
export function porModulo(tipos: readonly TipoAviso[]): { modulo: string; nombre: string; tipos: TipoAviso[] }[] {
    const grupos: { modulo: string; nombre: string; tipos: TipoAviso[] }[] = [];
    for (const t of tipos) {
        const g = grupos.find((x) => x.modulo === t.module);
        if (g === undefined) {
            grupos.push({ modulo: t.module, nombre: t.module_label ?? nombreDeModulo(t.module), tipos: [t] });
        } else {
            g.tipos.push(t);
        }
    }
    return grupos;
}

/** El código del módulo legible («reservas_sala» → «Reservas sala»): la API no manda su nombre. */
export function nombreDeModulo(modulo: string): string {
    const texto = modulo.replace(/[_-]+/g, ' ').trim();
    return texto === '' ? '' : `${texto.charAt(0).toLocaleUpperCase('es')}${texto.slice(1)}`;
}
